<script setup>
import { useClinical } from '@/composables/useClinical'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'medication-manage',
    subject: 'Clinical',
  },
})

const ability = useAbility()
const { medications, loading, saving, fetchMedications, createMedication, updateMedication, deleteMedication } = useClinical()

const highAlertOnly = ref(false)

const load = () => fetchMedications(highAlertOnly.value ? { is_high_alert: 1 } : {})

onMounted(load)
watch(highAlertOnly, load)

const dialog = ref(false)
const dialogTitle = ref('')
const editingId = ref(null)
const errors = ref({})

const formDefault = () => ({ name: '', category: '', is_high_alert: false, description: '', is_active: true })
const form = ref(formDefault())

const openCreate = () => {
  editingId.value = null
  form.value = formDefault()
  errors.value = {}
  dialogTitle.value = 'Tambah Obat'
  dialog.value = true
}

const openEdit = item => {
  editingId.value = item.id
  form.value = {
    name: item.name,
    category: item.category ?? '',
    is_high_alert: item.is_high_alert,
    description: item.description ?? '',
    is_active: item.is_active,
  }
  errors.value = {}
  dialogTitle.value = 'Edit Obat'
  dialog.value = true
}

const save = async () => {
  errors.value = {}
  try {
    if (editingId.value) await updateMedication(editingId.value, form.value)
    else await createMedication(form.value)
    dialog.value = false
    load()
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Save medication error:', e)
  }
}

const deleteDialog = ref(false)
const deletingItem = ref(null)
const deleting = ref(false)

const confirmDelete = item => { deletingItem.value = item; deleteDialog.value = true }

const doDelete = async () => {
  deleting.value = true
  try {
    await deleteMedication(deletingItem.value.id)
    deleteDialog.value = false
    load()
  } catch (e) {
    console.error('Delete medication error:', e)
  } finally {
    deleting.value = false
  }
}

const headers = [
  { title: 'Nama', key: 'name' },
  { title: 'Kategori', key: 'category' },
  { title: 'High Alert', key: 'is_high_alert', align: 'center' },
  { title: 'Status', key: 'is_active', align: 'center' },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: 100 },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Formularium
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Daftar obat & status high alert (§9.5)
            </p>
          </div>
          <VBtn
            v-if="ability.can('manage', 'Clinical')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Obat
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard class="mb-4">
      <VCardText>
        <VSwitch
          v-model="highAlertOnly"
          label="Tampilkan high alert saja"
          color="error"
          hide-details
        />
      </VCardText>
    </VCard>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="medications"
        :loading="loading"
        hide-default-footer
      >
        <template #item.category="{ item }">
          {{ item.category ?? '-' }}
        </template>
        <template #item.is_high_alert="{ item }">
          <HighAlertBadge :is-high-alert="item.is_high_alert" />
        </template>
        <template #item.is_active="{ item }">
          <VChip
            size="small"
            :color="item.is_active ? 'success' : 'secondary'"
          >
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('manage', 'Clinical')"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="primary"
            @click="openEdit(item)"
          />
          <VBtn
            v-if="ability.can('manage', 'Clinical')"
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            @click="confirmDelete(item)"
          />
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada data obat
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="560"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          {{ dialogTitle }}
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                label="Nama Obat *"
                :error-messages="errors.name"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.category"
                label="Kategori"
                placeholder="mis. antibiotik, analgesik"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.description"
                label="Deskripsi"
                rows="2"
              />
            </VCol>
            <VCol cols="12">
              <VSwitch
                v-model="form.is_high_alert"
                label="High Alert"
                color="error"
                hide-details
              />
            </VCol>
            <VCol cols="12">
              <VSwitch
                v-model="form.is_active"
                label="Aktif"
                color="success"
                hide-details
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="deleteDialog"
      max-width="420"
    >
      <VCard>
        <VCardTitle class="pa-6 pb-2">
          Hapus Obat?
        </VCardTitle>
        <VCardText class="pa-6 pt-0">
          <strong>{{ deletingItem?.name }}</strong> akan dihapus.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="deleting"
            @click="deleteDialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="error"
            :loading="deleting"
            @click="doDelete"
          >
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
